<template>
	<div>
		<!-- Headline -->
		<div slot="headline" class="fs5 lh125 fw7 c1 mb2">{{ $t("premium") }}</div>

		<j-card class="bg5">
			<!-- Gross Premium -->
			<j-list :list="computedGrossPremium" class="bb1 bc7">
				<div class="flex xs-12 p3" slot="list" slot-scope="{ item }">
					<div class="flex xs-6 xs-left xs-middle fw7 c1 lh125">
						{{ item.label }}
						<j-button
							v-if="hasPremiumActions"
							v-can:or="['writeQuotes', 'writePolicies']"
							size="xsmall"
							class="ml2"
							type="fifth"
							@click="$modal.show(`edit_gross_premium_${computedOfferId}`)"
						>{{ $t("edit_gross_premium") }}</j-button>
					</div>
					<div class="flex xs-6 xs-right xs-middle lh125">{{ item.value }}</div>
				</div>
			</j-list>

			<!-- Premium Loadings -->
			<div
				class="xs-12 p3 bb1 bc7"
				v-if="computedLoadings.length > 0 || hasLoadingsActions"
			>
				<div class="xs-12 fs3 lh125 fw7 c1">{{ $t("premium_loadings") }}</div>
				<j-list
					:list="computedLoadings"
					class="b1 bc7 mt2"
					v-if="computedLoadings.length > 0"
				>
					<div class="flex xs-12 p3" slot="list" slot-scope="{ item }">
						<div class="flex xs-6 xs-left xs-middle fw7 c6 lh125">{{ item.label }}</div>
						<div class="flex xs-6 xs-right xs-middle lh125">
							<div class="mr3 xs-right ta3">
								<div class="lh125">{{ item.value }}</div>
								<div class="c6 lh125">{{ item.summary }}</div>
							</div>
							<div v-if="hasLoadingsActions">
								<crm-icon
									name="delete"
									class="cu2"
									color="cError"
									size="small"
									@click.native="removeLoading(item.id)"
								/>
							</div>
						</div>
					</div>
				</j-list>

				<!-- Add button and loading state -->
				<div class="flex xs-12 xs-between" v-if="hasLoadingsActions">
					<j-button
						v-can:or="['writeQuotes', 'writePolicies']"
						size="xsmall"
						class="mt2"
						@click="$modal.show(`addLoading_${computedOfferId}`)"
						action="addLoading"
					>{{ $t("add_loading") }}</j-button>
					<!-- <div v-if="isLoading" class="fs3 c1 lh125 mt1">
						{{ $t("loading") }}
					</div>-->
				</div>
			</div>

			<!-- Discounts and Taxes -->
			<div
				class="xs-12 p3 bb1 bc7"
				v-if="computedDiscounts.length > 0 || hasDiscountActions"
			>
				<div class="xs-12 fs3 lh125 fw7 c1">{{ $t("discount_codes") }}</div>
				<j-list
					:list="computedDiscounts"
					class="b1 bc7 mt2"
					v-if="computedDiscounts.length > 0"
				>
					<div class="flex xs-12 p3" slot="list" slot-scope="{ item }">
						<div class="flex xs-6 xs-left xs-middle fw7 c6 lh125">{{ item.label }}</div>
						<div class="flex xs-6 wrap xs-right xs-middle lh125">
							<div class="mr3 xs-right ta3">
								<div class="lh125">{{ item.value }}</div>
								<div class="c6 lh125">{{ item.summary }}</div>
							</div>
							<crm-icon
								name="delete"
								class="cu2 remove-discount"
								color="cError"
								size="small"
								@click.native="removeDiscount(item)"
							/>
						</div>
					</div>
				</j-list>

				<!-- Add button and loading state -->
				<div class="flex xs-12 xs-between" v-if="hasDiscountActions">
					<j-button
						v-can:or="['writeQuotes', 'writePolicies']"
						size="xsmall"
						class="mt2"
						@click="$modal.show(`addDiscount_${computedOfferId}`)"
					>{{ $t("add_discount") }}</j-button>
				</div>
			</div>

			<div class="xs-12 p3 bb1 bc7" v-if="computedPriceBreakdown.length > 0">
				<div
					slot="header"
					class="xs-12 fs3 lh125 fw7 pb2"
				>{{ $t("price_breakdown") }}</div>
				<div slot="body">
					<j-list :list="computedPriceBreakdown" :itemClasses="'mb2 bg4 r2'">
						<div class="flex xs-12 p3" slot="list" slot-scope="{ item }">
							<div class="flex xs-6 xs-left xs-middle fw7 c1 lh125">{{ item.label }}</div>
							<div class="xs-6 ta3">
								<div class="lh125">{{ item.value }}</div>
								<div class="c6 lh125">{{ item.summary }}</div>
							</div>
						</div>
					</j-list>
				</div>
			</div>

			<!-- Net Premium -->
			<j-list :list="computedNetPremium">
				<div
					class="flex xs-12 p3 bg7--l4 bc7 bt1"
					slot="list"
					slot-scope="{ item }"
				>
					<div class="flex xs-6 xs-left xs-middle fw7 c1 lh125">
						{{ item.label }}
						<!-- TODO: Temporary disabled :) -->
						<!-- <j-button
							v-if="hasPremiumActions"
							v-can:or="['writeQuotes', 'writePolicies']"
							size="xsmall"
							type="fifth"
							class="ml2"
							@click="$modal.show(`edit_net_premium_${computedOfferId}`)"
						>
							{{ $t("edit_net_premium") }}
						</j-button>-->
					</div>
					<div class="flex xs-6 xs-right xs-middle lh125 fw7">{{ item.value }}</div>
				</div>
			</j-list>
		</j-card>

		<!-- Modal for adding loading -->
		<core-modal
			:name="`addLoading_${computedOfferId}`"
			:headline="$t('add_loading')"
		>
			<premium-loading-create
				:policyId="policyId"
				:offerId="offerId"
				:productId="productId"
				@created="onCreated('loading')"
				@cancelled="$modal.hide(`addLoading_${computedOfferId}`)"
			/>
		</core-modal>

		<core-modal
			:name="`addDiscount_${computedOfferId}`"
			:headline="$t('add_discount')"
		>
			<add-discount
				:policyId="policyId"
				:offerId="offerId"
				:productId="productId"
				:pricing="pricing"
				@created="onCreated('discount')"
				@cancelled="$modal.hide(`addDiscount_${computedOfferId}`)"
			/>
		</core-modal>

		<core-modal
			:name="`edit_gross_premium_${computedOfferId}`"
			:headline="$t('edit_gross_premium')"
		>
			<div class="p4">
				<div class="lh150">
					<div class="lh150">
						<span class>{{ $t("edit_gross_premium_description") }}</span>
						<p
							class="pt2 pb2"
						>{{ $t("gross_premium") }}: {{ computedGrossPremium[0].value }}</p>
					</div>
				</div>
				<update-premium
					type="gross"
					:isLoading="isLoading"
					:amount="this.pricing.originalPrice"
					@created="onUpdatePremium"
					@cancelled="$modal.hide(`edit_gross_premium_${computedOfferId}`)"
				/>
			</div>
		</core-modal>

		<core-modal
			:name="`edit_net_premium_${computedOfferId}`"
			:headline="$t('edit_net_premium')"
		>
			<div class="p4">
				<div class="lh150">
					<div class="lh150">
						<span>{{ $t("edit_net_premium_description") }}</span>
						<p
							class="pt2 pb2"
						>{{ $t("net_premium") }}: {{ computedNetPremium[0].value }}</p>
					</div>
				</div>

				<update-premium
					type="net"
					:isLoading="isLoading"
					:amount="this.pricing.amount"
					@created="onUpdatePremium"
					@cancelled="$modal.hide(`edit_net_premium_${computedOfferId}`)"
				/>
			</div>
		</core-modal>
	</div>
</template>

<script>
import JCard from "@/components/JCard"
import JList from "@/components/JList"
import JButton from "@/components/JButton"
import JExpendableBox from "@/components/JExpendableBox/"

import CrmIcon from "@/components/CrmIcon"
import PremiumLoadingCreate from "./PremiumLoadingCreate"
import AddDiscount from "./AddDiscount"
import UpdatePremium from "./UpdatePremium"
import CoreModal from "@/modules/core/components/CoreModal"
import dayjs from "dayjs"
import get from "lodash.get"
import api from "../api/"

export default {
	components: {
		JCard,
		JList,
		JButton,
		CrmIcon,
		PremiumLoadingCreate,
		CoreModal,
		JExpendableBox,
		AddDiscount,
		UpdatePremium
	},

	props: {
		policyId: {
			type: String,
			required: true,
			default: ""
		},

		offerId: {
			validator(val) {
				return val === null || typeof val === "string"
			},
			required: false,
			default: null
		},

		productId: {
			type: Object,
			required: true,
			default: () => ({
				plan: null,
				type: null,
				version: null
			})
		},

		/**
		 * When this prop is true, we display loadings actions such as create and delete
		 */
		hasLoadingsActions: {
			type: Boolean,
			required: false,
			default: false
		},

		hasDiscountActions: {
			type: Boolean,
			required: false,
			default: false
		},

		hasPremiumActions: {
			type: Boolean,
			required: false,
			default: false
		},

		/**
		 * This is a type pricing we get from GraphQl on policies.acceptedOffer.premium
		 */
		pricing: {
			type: Object,
			required: true,
			default: () => ({
				amount: null,
				currencyCode: null,
				formattedPrice: null,
				originalPrice: null,
				formattedOriginalPrice: null,
				appliedTaxes: {
					code: null,
					ratio: null,
					name: null,
					order: null,
					flat: null,
					formattedFlat: null,
					formattedOriginalPrice: null
				},
				appliedDiscounts: {
					code: null,
					ratio: null,
					flat: null,
					formattedFlat: null,
					order: null,
					formattedOriginalPrice: null
				},
				loadings: {
					id: null,
					codeName: {
						code: null,
						name: null
					},
					ratio: null,
					flat: null
				}
			})
		}
	},

	data() {
		return {
			isLoading: false,
			expanded: false
		}
	},

	computed: {
		computedOfferId() {
			return this.offerId
		},
		computedGrossPremium() {
			return [
				{
					label: this.$t("gross_premium"),
					value: get(this, "pricing.formattedOriginalPrice", null)
				}
			]
		},

		computedNetPremium() {
			return [
				{
					label: this.$t("net_premium"),
					value: get(this, "pricing.formattedPrice", null)
				}
			]
		},

		computedDiscounts() {
			const appliedDiscounts = this.pricing.appliedDiscounts
				? this.pricing.appliedDiscounts
				: []

			const getValue = item => {
				if (!item) return ""
				const ratio = item.ratio ? item.ratio * 100 : null
				const flat = item.formattedFlat ? item.formattedFlat : ""
				return {
					flat,
					summary: `(${ratio.toFixed(2)}% of ${item.formattedOriginalPrice})`
				}
			}

			return [
				...appliedDiscounts.map(item => ({
					label: item.name ? item.name : item.code,
					value: getValue(item).flat,
					summary: getValue(item).summary
				}))
			]
		},

		computedTaxes() {
			const appliedTaxes = this.pricing.appliedTaxes
				? this.pricing.appliedTaxes
				: []

			const getValue = item => {
				if (!item) return ""
				const ratio = item.ratio ? `${item.ratio * 100}%` : null
				const flat = item.formattedFlat ? item.formattedFlat : "0"
				return {
					flat,
					summary: ratio ? `(${ratio} of ${item.formattedOriginalPrice})` : ""
				}
			}

			return [
				...appliedTaxes.map(item => ({
					label: item.name ? item.name : item.code,
					value: getValue(item).flat,
					summary: getValue(item).summary
				}))
			]
		},

		computedPriceBreakdown() {
			// Combine taxes and discounts together and order based on order
			const appliedDiscounts = this.pricing.appliedDiscounts
				? this.pricing.appliedDiscounts
				: []
			const appliedTaxes = this.pricing.appliedTaxes
				? this.pricing.appliedTaxes
				: []

			const taxesAndDiscounts = [...appliedDiscounts, ...appliedTaxes].sort(
				(a, b) => a.order - b.order
			)

			const getValue = item => {
				if (!item) return ""
				const ratio = item.ratio ? item.ratio * 100 : null
				const flat = item.formattedFlat ? item.formattedFlat : "0"
				return {
					flat,
					summary: ratio
						? `(${ratio.toFixed(2)}% of ${item.formattedOriginalPrice})`
						: ""
				}
			}

			const loadings = get(this, "pricing.loadings", []) || []

			// prettier-ignore
			return [
				...loadings.map(loading => ({
					label: `${get(loading, "codeName.code", "")} - ${get(loading, "codeName.name", "")}`,
					value: `${get(this, "pricing.currencyCode", "") || ""}${loading?.flat?.toFixed(2) || "0"}`,
					summary: `(${loading.ratio ? loading.ratio.toFixed(2) * 100 : null || "0"}% of ${this.pricing.formattedOriginalPrice})`
				})),
				...taxesAndDiscounts.map(item => ({
					label: item.name ? item.name : item.code,
					value: getValue(item).flat,
					summary: getValue(item).summary
				}))
			]
		},

		computedLoadings() {
			const loadings = get(this, "pricing.loadings", [])
			if (!loadings) return []

			/**
			 * Helper function that accepts loading type
			 * and decides whether to show flat or ratio
			 */
			const getValue = item => {
				if (!item) return ""
				const ratio = item.ratio ? `${(item.ratio * 100).toFixed(2)}%` : null
				const flat = item.flat
					? `${get(this, "pricing.currencyCode", "") || ""}${item.flat}`
					: "0"
				return {
					flat,
					summary: ratio
						? `(${ratio} of ${this.pricing.formattedOriginalPrice})`
						: ""
				}
			}

			return loadings.map(loading => ({
				id: loading.id,
				label: `${get(loading, "codeName.code", "")} -
					${get(loading, "codeName.name", "")}`,
				value: getValue(loading).flat,
				summary: getValue(loading).summary
			}))
		}
	},

	methods: {
		async removeDiscount(item) {
			this.isLoading = true
			const variables = {
				policyId: this.policyId,
				offerId: this.offerId,
				updateOfferInput: {
					productId: this.productId,
					premium: {
						discountCodes: this.pricing.appliedDiscounts
							.filter(discount => discount.code !== item.label)
							.map(discount => discount.code)
					}
				}
			}
			const res = await api.updateOffer(variables)
			this.isLoading = false
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
				return
			}
			this.$emit("discount:deleted")
		},
		async removeLoading(id) {
			this.isLoading = true
			const res = await api.removeLoading({
				policyId: this.policyId,
				offerId: this.offerId,
				loadingId: id
			})
			this.isLoading = false

			// Check errors
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
				return
			}

			// Emit
			this.$emit("loading:deleted")
		},

		onCreated(type) {
			this.$emit(`${type}:created`)
			this.$modal.hide(
				`add${type[0].toUpperCase()}${type.slice(1)}_${this.offerId}`
			)
		},

		async onUpdatePremium({ type, amount }) {
			this.isLoading = true

			const premium = {
				currencyCode: this.pricing.currencyCode // we have to save currency code as well otherwise things will break
			}

			if (type === "gross") {
				premium.grossAmount = amount
				premium.discountCodes = this.pricing.appliedDiscounts.map(
					discount => discount.code
				)
			} else if (type === "net") {
				premium.amount = amount
			}

			const variables = {
				policyId: this.policyId,
				offerId: this.offerId,
				updateOfferInput: {
					productId: this.productId,
					premium
				}
			}

			const res = await api.updateOffer(variables)
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
				return
			}

			this.$emit("premium:updated")

			setTimeout(() => {
				this.$modal.hide(`edit_${type}_premium_${this.offerId}`)
				this.isLoading = false
			}, 500)
		}
	}
}
</script>
